import { App } from "@capacitor/app"
import { env } from "@/shared/config/env"
import {
	detectPlatform,
	detectStore,
	getNativeDeviceInfo,
} from "@/shared/lib/platform"
import { getDeviceTimezone } from "@/shared/lib/timezone"

/**
 * Поля юзера для диагностики. Локальный тип, а не `User` из entities:
 * shared — самый нижний слой FSD и не может зависеть от entities.
 */
export type DiagnosticsUser = {
	id?: string | number | null
	luni_id?: string | null
	luni_name?: string | null
	city?: string | null
	country_ISO_code?: string | null
}

/**
 * Собирает данные об устройстве и юзере для обращения в поддержку.
 * На нативе устройство описывают `App` / `Device` из Capacitor, на web —
 * сырой `navigator.userAgent` (web-реализация `Device.getInfo()` парсит UA
 * криво и отдаёт мусор вида `Mozilla/5.0 (, android undefined`).
 * Поля юзера — best-effort: на краш-экране кэш может быть пуст.
 * IP/geo намеренно не тянем — это внешний запрос, ненадёжный на краше.
 *
 * Вызывать заранее (в useEffect), а не внутри обработчика клика:
 * await до `clipboard.writeText` теряет user activation в iOS WKWebView.
 */
export async function collectDiagnostics(
	user?: DiagnosticsUser | null,
): Promise<string> {
	const timezone = getDeviceTimezone()
	const platform = detectPlatform()
	const store = detectStore()
	const lines = [
		user?.luni_id && `Luni ID: ${user.luni_id}`,
		user?.id && `User ID: ${user.id}`,
		user?.luni_name && `Name: ${user.luni_name}`,
		user?.city && `City: ${user.city}`,
		user?.country_ISO_code && `Country: ${user.country_ISO_code}`,
		`Platform: ${platform}`,
		store && `Store: ${store}`,
		env.VITE_PUBLIC_APP_VERSION && `Version: ${env.VITE_PUBLIC_APP_VERSION}`,
		timezone && `TZ: ${timezone}`,
	]

	if (platform !== "ios" && platform !== "android") {
		lines.push(`UA: ${navigator.userAgent}`)
		return lines.filter(Boolean).join("\n")
	}

	try {
		const app = await App.getInfo()
		lines.push(`App: ${app.version} (${app.build})`)
	} catch {}

	const info = await getNativeDeviceInfo()
	if (info) {
		const { manufacturer, model, operatingSystem, osVersion } = info
		const device = [manufacturer, model, operatingSystem, osVersion]
			.filter(Boolean)
			.join(" ")
		if (device) lines.push(`Device: ${device}`)
	}

	return lines.filter(Boolean).join("\n")
}
